/**
 * Widget Conditional Inputs - Isolated Version
 *
 * A fork of ConditionalInputs that uses only eg-* prefixed components
 * without any Bootstrap dependencies.
 */

import { useEffect, useState } from "react";
import _ from "lodash";
import { hdlChg, hdlSchm, hdlIsVal } from '../WidgetPreviewForm';
import { checkTypeOfConditional, resetInputsCatalogues, returnFormData, returnIsValid } from "../../components/Form/PreviewForm/formStarter";

// This will be passed as a prop since WidgetRenderInput is in WidgetPreviewForm
export const WidgetConditionalInputs = ({
  RenderInputComponent, // Pass WidgetRenderInput as a prop to avoid circular dependency
  entireSchema,
  entirePathSchema,
  setSchemaState,
  entireFormData,
  entirePathData,
  setFormData,
  entireIsValid,
  setIsValid,
  doValidate,
  conditionals = [],
  parentValue,
  parentOrigin,
  constraints,
  setConstraints,
  attachments,
  setAttachments,
  tryToNext,
  activeStep,
  answered,
  lang,
  theme = 'light',
  formParams = {}
}) => {
  const [currentConditionals, setCurrentConditionals] = useState([]);
  const [currentConditionalsIdx, setCurrentConditionalsIdx] = useState(null);
  const [rerendering, setRerendering] = useState(false);
  const [parentValueState, setParentValueState] = useState(parentValue);

  function changeValidationForConditionals(getCurrentConditionals, getCurrentConditionalsIdx) {
    if (getCurrentConditionalsIdx != currentConditionalsIdx) {
      const newSchemaConditionals = Array.isArray(getCurrentConditionals) && resetInputsCatalogues(getCurrentConditionals) || [];
      const [newDataConditionals, newValidConditionals] = searchSubjectAndDescription(getCurrentConditionals, getCurrentConditionalsIdx);
      hdlSchm({ action: "resetInputs", pathSchema: `${entirePathData}.conditionals.${getCurrentConditionalsIdx}.nestChildren`, inputs: newSchemaConditionals, entireSchema, setSchemaState });
      hdlChg({ e: { target: { value: newDataConditionals } }, params: { conditionals: newDataConditionals }, entireFormData, entirePathData, setFormData });
      hdlIsVal({ errors: newValidConditionals, entirePathDataWithKey: `${entirePathData}.conditionals`, entireIsValid, setIsValid });
      setParentValueState(parentValue);
      setRerendering(true);
    }
  }

  function changeSchema() {
    if (!rerendering) {
      const getCurrentConditionals = conditionals.find(item => checkTypeOfConditional(item, parentValue));
      const getCurrentConditionalsIdx = conditionals.findIndex(item => checkTypeOfConditional(item, parentValue));
      setCurrentConditionals(getCurrentConditionals?.nestChildren);
      setCurrentConditionalsIdx(getCurrentConditionalsIdx);
      if (parentValueState != parentValue) {
        changeValidationForConditionals(getCurrentConditionals?.nestChildren, getCurrentConditionalsIdx);
      }
    }
  }

  function searchSubjectAndDescription(conditionals, condicionalsIdx) {
    let newDataConditionals = Array.isArray(conditionals) && returnFormData(conditionals, `${parentOrigin}::conditionals::${condicionalsIdx}::nestChildren`) || [];
    let newValidConditionals = Array.isArray(conditionals) && returnIsValid(conditionals, lang) || [];

    const conditionlasFinal = (conditionals, target) => {
      return conditionals.map((input, idx) => {
        const pathSchema = `${entirePathSchema}.conditionals.${condicionalsIdx}.nestChildren.${idx}`;
        const schema = _.get(entireSchema, `${pathSchema}`);
        if (!(schema.key === "subject" || schema.key === "description")) {
          return input;
        } else {
          const pathData = `${entirePathData}.conditionals`;
          const previousData = _.get(entireFormData, `${pathData}`);
          if (!Array.isArray(previousData)) return input;
          const findedPrev = previousData.find(item => schema.key in item);
          if (findedPrev) {
            const value = findedPrev[schema.key];
            const errors = target === "data" ? null : doValidate(value, schema.key, schema.required);
            return { ...input, [schema.key]: target === "data" ? value : errors };
          } else {
            return input;
          }
        }
      });
    };

    const newDataConditionalsFinal = conditionlasFinal(newDataConditionals, "data");
    const newValidConditionalsFinal = conditionlasFinal(newValidConditionals, "valid");

    return [newDataConditionalsFinal, newValidConditionalsFinal];
  }

  useEffect(() => {
    if (rerendering) {
      setRerendering(false);
    }
  }, [rerendering]);

  useEffect(() => {
    changeSchema();
  }, [parentValue]);

  if (!RenderInputComponent) {
    console.warn('[Widget] RenderInputComponent not provided to WidgetConditionalInputs');
    return null;
  }

  return (
    <>
      {(Array.isArray(currentConditionals)) &&
        (currentConditionals.length > 0) &&
        (currentConditionalsIdx >= 0) &&
        !rerendering &&
        currentConditionals.map((sch, idx) => (
          <RenderInputComponent
            key={`${parentOrigin}::conditionals::${currentConditionalsIdx}::nestChildren::${idx}`}
            entireSchema={entireSchema}
            entirePathSchema={`${entirePathSchema}.conditionals.${currentConditionalsIdx}.nestChildren.${idx}`}
            setSchemaState={setSchemaState}
            entireFormData={entireFormData}
            entirePathData={`${entirePathData}.conditionals.${idx}`}
            setFormData={setFormData}
            entireIsValid={entireIsValid}
            setIsValid={setIsValid}
            constraints={constraints}
            setConstraints={setConstraints}
            attachments={attachments}
            setAttachments={setAttachments}
            tryToNext={tryToNext}
            activeStep={activeStep}
            origin={`${parentOrigin}::conditionals::${currentConditionalsIdx}::nestChildren::${idx}`}
            answered={answered}
            lang={lang}
            theme={theme}
            formParams={formParams}
          />
        ))}
    </>
  );
};

export default WidgetConditionalInputs;
